import { Button, Step, StepLabel, Stepper, Typography, useMediaQuery } from "@mui/material";
import { FC, Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import EpiEmployeeHeader from "../../components/epi/EpiEmployeeHeader";
import { EpiInspectionFlowService } from "../../services/EpiInspectionFlowService";
import { EpiInspectionFlowDTO, EpiInspectionItemDTO } from "../../types/EpiInspectionFlowDTO";
import { getLoading } from "../execution-events/ExecutionEvents";
import './EpiInspectionWizard.scss';
import EpiChecklistStep from "./steps/EpiChecklistStep";
import EpiExamsStep from "./steps/EpiExamsStep";
import EpiSignatureStep from "./steps/EpiSignatureStep";
import EpiSummaryStep from "./steps/EpiSummaryStep";

export interface StepProps {
	inspection: EpiInspectionFlowDTO;
	onSave: (step: number, items: EpiInspectionItemDTO[],
		generalObservation?: string) => Promise<boolean>;
	onInspection: (inspection: EpiInspectionFlowDTO) => void;
}

const stepLabels = ['Checklist de EPIs', 'Resumo e ciência', 'Pendências de exames', 'Assinaturas'];

const EpiInspectionWizard: FC = () => {
	const [inspection, setInspection] = useState<EpiInspectionFlowDTO>();
	const [loading, setLoading] = useState(true);
	const { inspectionId } = useParams();
	const navigate = useNavigate();
	const isMobile = useMediaQuery('(max-width:768px)');
	const { showError } = useAlert();
	const service = new EpiInspectionFlowService();

	useEffect(() => {
		const load = async () => {
			const response = await service.get(Number(inspectionId));
			setInspection(response?.data);
			setLoading(false);
		};
		load();
	}, []);

	if (loading) {
		return (
			<Fragment>
				<AppBar
					modulo="Inspeção de EPIs"
				/>
				{getLoading()}
			</Fragment>
		);
	}

	if (!inspection) {
		return (
			<Fragment>
				<AppBar
					modulo="Inspeção de EPIs"
				/>
				<div className="epi-inspection-wizard">
					<Typography variant="h6">Inspeção não encontrada.</Typography>
					<Button variant="contained" onClick={() => navigate('/inspecao-epis/nova')}>
						Voltar
					</Button>
				</div>
			</Fragment>
		);
	}

	const current = inspection.currentStep || 0;

	return (
		<Fragment>
			<AppBar
				modulo="Inspeção de EPIs"
			/>
			<div className="epi-inspection-wizard">
				<EpiEmployeeHeader
					name={inspection.employeeName}
					registration={inspection.employeeRegistration}
					position={inspection.positionName}
					sector={inspection.sectorName}
					inspectionDate={inspection.inspectionDate}
					inspectionNumber={inspection.number}
				/>
				<Stepper activeStep={current} orientation={isMobile ? 'vertical' : 'horizontal'}>
					{stepLabels.map(label => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
				<div className="conteudo">
					{current === 0 && <EpiChecklistStep inspection={inspection} onSave={save} onInspection={setInspection} />}
					{current === 1 && <EpiSummaryStep inspection={inspection} onSave={save} onInspection={setInspection} />}
					{current === 2 && <EpiExamsStep inspection={inspection} onSave={save} onInspection={setInspection} />}
					{current === 3 && <EpiSignatureStep
						inspection={inspection}
						onSave={save}
						onInspection={setInspection}
					/>}
				</div>
			</div>
		</Fragment>
	);

	async function save(step: number, items: EpiInspectionItemDTO[],
			generalObservation?: string) {
		try {
			const response = await service.saveStep(inspection!.id, step,
				generalObservation !== undefined ? generalObservation : inspection!.generalObservation,
				items);
			setInspection(response.data);
			return true;
		} catch (error: any) {
			showError(error?.response?.data || 'Não foi possível salvar a inspeção.');
			return false;
		}
	}
};

export default EpiInspectionWizard;
